import { IOrder, OrderStatus } from "../types/order/IOrder";
import { sortOrders } from "./orderSorting";

interface OrderListFilters {
  workspaceId: string;
  statuses: OrderStatus[];
  driverId?: string;
  search: string;
  missingOnly: boolean;
  completeness?: Record<string, { needsAttention: boolean }>;
}

/**
 * The existing workspace listener is authoritative for both membership and
 * values. Filter and sort the full snapshot BEFORE slicing the visible page,
 * so new orders and filter-changing edits are never hidden behind a stale API
 * cursor. The caller limits how many rows are rendered, without extra reads.
 */
export const selectOrderListSource = <T extends IOrder>(
  realtimeOrders: T[],
  filters: OrderListFilters,
): T[] => {
  const term = filters.search.trim().toLowerCase();
  return sortOrders(
    realtimeOrders.filter((order) => {
      if (order.workspaceId !== filters.workspaceId) return false;
      if (filters.statuses.length && !filters.statuses.includes(order.status))
        return false;
      if (filters.driverId && order.driverId !== filters.driverId) return false;
      if (
        filters.missingOnly &&
        !filters.completeness?.[order.id]?.needsAttention
      )
        return false;
      if (!term) return true;

      // Keep the API's customer-prefix search semantics, including its fallback
      // for private customers and older orders without a customer name.
      const customer = order.customer;
      const name = customer?.isCompany
        ? customer.name
        : `${customer?.firstName || ""} ${customer?.lastName || ""}`.trim() ||
          customer?.name;
      return (name || order.orderNumber || "")
        .trim()
        .toLowerCase()
        .startsWith(term);
    }),
    "created-desc",
  );
};
